import { CENTS_PER_KM_CAP_KM, CENTS_PER_KM_RATES, calculateCentsPerKm } from "@stb/jurisdiction-au"

import {
  capKm,
  displayYear,
  formatCheckedOn,
  HOME_FINANCIAL_YEAR,
  rate,
} from "../../components/home/home-content"
import { formatAud, SimpleTable, SourceLine } from "../../components/landing"
import type { Guide } from "../guide-types"

const first = CENTS_PER_KM_RATES[0]
const checked = formatCheckedOn(rate.checkedOn)
const newest = displayYear(HOME_FINANCIAL_YEAR)
const years = CENTS_PER_KM_RATES.toReversed()

const maxClaim = (year: string) => {
  const r = calculateCentsPerKm(year, CENTS_PER_KM_CAP_KM)
  return r.ok ? formatAud(r.deduction) : ""
}

export const rateHistoryGuide: Guide = {
  slug: "ato-cents-per-km-rate-history",
  title: `ATO cents per km rates by year, ${displayYear(first?.financialYear ?? "")} to ${newest}`,
  description: `Every ATO cents per kilometre rate from ${displayYear(first?.financialYear ?? "")} to ${newest}, with the maximum claim at ${capKm} km and a link to the ATO source.`,
  crumb: "Cents per km rate history",
  h1: "ATO cents per kilometre rates by year",
  published: "2026-10-27",
  reviewed: "2026-10-27",
  financialYear: `${displayYear(first?.financialYear ?? "")} to ${newest}`,
  checked,
  sources: [{ label: "ATO, cents per kilometre method (individuals)", href: rate.sourceUrl }],
  answer: (
    <>
      The ATO rate was {first?.cents} cents per kilometre for{" "}
      {displayYear(first?.financialYear ?? "")} and is {rate.cents} cents for {newest}. Each year
      you can claim up to {capKm} business kilometres per car at that year&apos;s rate.
    </>
  ),
  related: [
    { label: "Mileage tracker: automatic GPS logbook", href: "/mileage-tracker" },
    { label: "Cents per km rate and calculator", href: "/cents-per-km-calculator" },
    { label: "Logbook method: when it pays off", href: "/logbook-method" },
  ],
  toc: [{ id: "rates", label: "Rates by year" }],
  body: (
    <section aria-labelledby="rates" className="grid gap-3">
      <h2 id="rates" className="text-2xl font-semibold">
        Rates by year
      </h2>
      <SimpleTable
        caption="Rates by year"
        columns={["Income year", "Rate", `Maximum claim at ${capKm} km`]}
        rows={years.map((r) => ({
          header: displayYear(r.financialYear),
          cells: [`${r.cents} c/km`, maxClaim(r.financialYear)],
        }))}
      />
      <SourceLine
        sources={[{ label: "ATO", href: rate.sourceUrl }]}
        financialYear={`${displayYear(first?.financialYear ?? "")} to ${newest}`}
        checked={checked}
      />
      <p className="text-muted-foreground text-sm">
        The maximum claim is our arithmetic (rate × {capKm} km), not a figure the ATO publishes.
      </p>
      <p>
        <a
          href="/cents-per-km-calculator"
          className="focus-visible:ring-ring rounded-sm underline underline-offset-2 outline-none focus-visible:ring-2"
        >
          Current rate and calculator
        </a>
      </p>
    </section>
  ),
}
